import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Alert, Box, Chip, useTheme } from '@mui/material';
import {
  MaterialReactTable,
  type MRT_PaginationState,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
  type MRT_ColumnSizingState,
  type MRT_ColumnPinningState,
} from 'material-react-table';
import { useAuth } from '../components/Auth/AuthProvider';
import { useTenant } from '../components/TenantContext';
import { useSharedViews } from '../hooks/useSharedViews';
import { formatMetricValue } from '../components/MetricCards';
import { ReportMetricsBar } from '../components/ReportMetricsBar';
import { useNotification } from '../components/NotificationProvider';
import type { DailyReportSummary } from '../types/reportSummaries';
import {
  type AflDailyRow,
  columns as baseColumns,
  getInitialColumnVisibility,
  getDefaultColumnOrder,
  sanitizeColumnVisibility,
} from './reports/columns';
import { getDefaultDateRange } from '../lib/dateUtils';
import { apiClient } from '../lib/api';
import {
  createFilterState,
  mergeColumnOrder,
  sanitizeColumnFilters,
  normalizeSavedViewConfig,
  deduplicateViews,
  pruneOldViews,
  arraysEqual,
  type ReportFiltersState,
  type SavedViewState,
  type ProviderSource,
} from './reports/viewState';
import { useDynamicColumns } from './reports/useDynamicColumns';
import { useReportData } from './reports/useReportData';
import { useReportViewManager } from './reports/useReportViewManager';
import { useReportTable } from './reports/useReportTable';
import { ReportTopToolbar } from './reports/ReportTopToolbar';

type SavedView = SavedViewState;

const MAX_SAVED_VIEWS = 50;

export const Reports: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant, setSwitching } = useTenant();
  const theme = useTheme();
  const { showError } = useNotification();
  const lastManualFiltersRef = useRef<ReportFiltersState | null>(null);
  const hydratedRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);

  // Shared views hook
  const {
    state: { sharedViews, viewTemplates, userPermissions },
    createSharedView, updateSharedView, deleteSharedView,
    trackViewUsage, refreshViewTemplates, updateViewTemplate,
  } = useSharedViews('afl-daily');

  // ── Core state ────────────────────────────────────────────────────────
  const [date, setDate] = useState<{ startDate: string; endDate: string }>(getDefaultDateRange);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'Date', desc: false }]);
  const [error, setError] = useState('');

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('afl_column_visibility');
    if (saved) {
      const sanitized = sanitizeColumnVisibility(JSON.parse(saved));
      localStorage.setItem('afl_column_visibility', JSON.stringify(sanitized));
      return sanitized;
    }
    return getInitialColumnVisibility(true);
  });
  const [density, setDensity] = useState<'compact' | 'comfortable' | 'spacious'>(
    () => (localStorage.getItem('afl_density') as any) || 'compact',
  );
  const [compactHeaders, setCompactHeaders] = useState(() => {
    const saved = localStorage.getItem('afl_compact_headers');
    return saved ? saved === '1' : true;
  });
  const [zebraRows, setZebraRows] = useState(() => {
    const saved = localStorage.getItem('afl_zebra_rows');
    return saved ? saved === '1' : true;
  });
  // const [providerSource, setProviderSource] = useState<ProviderSource>(() => {
  //   try {
  //     const saved = localStorage.getItem('afl_provider_source');
  //     return saved === 'opentrack' || saved === 'dealer_vault' ? saved : 'both';
  //   } catch { return 'both'; }
  // });

  const [providerSource, setProviderSource] = useState<ProviderSource>(() => 'opentrack');

  const [filterState, setFilterState] = useState<ReportFiltersState>(() => createFilterState());
  const { typeCodeFilter, lineTypeFilter, linePayFilter, coveredLineFilter, columnFilters } = filterState;

  const setTopFilter = useCallback(
    (key: 'typeCodeFilter' | 'lineTypeFilter' | 'linePayFilter' | 'coveredLineFilter', nextValue: string[]) => {
      setFilterState((prev) => {
        if (arraysEqual(prev[key], nextValue)) return prev;
        return { ...prev, [key]: [...nextValue] };
      });
    }, [],
  );
  const setTypeCodeFilter = useCallback((next: string[]) => setTopFilter('typeCodeFilter', next), [setTopFilter]);
  const setLineTypeFilter = useCallback((next: string[]) => setTopFilter('lineTypeFilter', next), [setTopFilter]);
  const setLinePayFilter = useCallback((next: string[]) => setTopFilter('linePayFilter', next), [setTopFilter]);
  const setCoveredLineFilter = useCallback((next: string[]) => setTopFilter('coveredLineFilter', next), [setTopFilter]);

  const setColumnFilters = useCallback(
    (next: MRT_ColumnFiltersState | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)) => {
      setFilterState((prev) => {
        const value = typeof next === 'function' ? next(prev.columnFilters) : next;
        if (value === prev.columnFilters) return prev;
        return { ...prev, columnFilters: value };
      });
    }, [],
  );

  const [columnSizing, setColumnSizing] = useState<MRT_ColumnSizingState>(() => {
    const saved = localStorage.getItem('afl_column_sizing');
    return saved ? JSON.parse(saved) : {};
  });
  const [columnPinning, setColumnPinning] = useState<MRT_ColumnPinningState>(() => {
    const saved = localStorage.getItem('afl_column_pinning');
    return saved ? JSON.parse(saved) : { left: ['Date', 'RO Number'], right: [] };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const defaultOrder = getDefaultColumnOrder();
    try {
      const saved = JSON.parse(localStorage.getItem('afl_column_order') || '[]');
      const { order, didChange } = mergeColumnOrder(saved, defaultOrder);
      if (didChange) localStorage.setItem('afl_column_order', JSON.stringify(order));
      return order;
    } catch { return defaultOrder; }
  });

  const [savedViews, setSavedViews] = useState<SavedView[]>(() => {
    try {
      const views = JSON.parse(localStorage.getItem('afl_saved_views') || '[]');
      if (!Array.isArray(views)) return [];
      return pruneOldViews(deduplicateViews(views), MAX_SAVED_VIEWS);
    } catch { return []; }
  });
  const [selectedViewName, setSelectedViewName] = useState(
    () => localStorage.getItem('afl_selected_view') || '',
  );

  const userId = useMemo(() => {
    if (user?.id) return user.id;
    try {
      const stored = localStorage.getItem('afl_user_id');
      if (stored) return stored;
      const newId = `anon_${Date.now()}`;
      localStorage.setItem('afl_user_id', newId);
      return newId;
    } catch { return `anon_${Date.now()}`; }
  }, [user]);

  // ── Persist provider source ───────────────────────────────────────────
  useEffect(() => {
    try { localStorage.setItem('afl_provider_source', providerSource); } catch { /* ignore */ }
  }, [providerSource]);

  // ── Compact headers attribute ─────────────────────────────────────────
  useEffect(() => {
    document.documentElement.toggleAttribute('data-compact-headers', compactHeaders);
  }, [compactHeaders]);

  // ── Ensure column order has all columns ───────────────────────────────
  useEffect(() => {
    if (!columnOrder?.length) {
      const defaultOrder = getDefaultColumnOrder();
      setColumnOrder(defaultOrder);
      localStorage.setItem('afl_column_order', JSON.stringify(defaultOrder));
      return;
    }
    const next = [...columnOrder];
    let changed = false;
    const fullDefault = getDefaultColumnOrder();
    for (const colId of fullDefault) {
      if (!next.includes(colId)) {
        let insertIdx = next.length;
        const defIdx = fullDefault.indexOf(colId);
        for (let i = defIdx + 1; i < fullDefault.length; i++) {
          const found = next.indexOf(fullDefault[i]);
          if (found !== -1) { insertIdx = found; break; }
        }
        next.splice(insertIdx, 0, colId);
        changed = true;
      }
    }
    if (changed) {
      setColumnOrder(next);
      localStorage.setItem('afl_column_order', JSON.stringify(next));
    }
  }, [columnOrder]);

  // ── Server-side preference sync ───────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchPrefs = user
      ? apiClient.get('/api/prefs/report/authenticated?reportId=afl-daily')
      : fetch(
          new URL('/api/prefs/report?reportId=afl-daily', (import.meta.env.VITE_API_URL as string) || window.location.origin).toString(),
          { headers: { 'Content-Type': 'application/json', 'x-user-id': userId, 'x-tenant-slug': (import.meta.env.VITE_TENANT_SLUG as string) || 'ourisman-va_alexandria-ford' } },
        );

    fetchPrefs
      .then((r) => (r.json ? r.json() : r))
      .then(({ settings }) => {
        if (cancelled || !settings) return;
        if (settings.date) setDate(settings.date);
        if (settings.globalFilter) setGlobalFilter(settings.globalFilter);
        if (settings.columnOrder?.length) { setColumnOrder(settings.columnOrder); localStorage.setItem('afl_column_order', JSON.stringify(settings.columnOrder)); }
        if (settings.columnVisibility) { const s = sanitizeColumnVisibility(settings.columnVisibility); setColumnVisibility(s); localStorage.setItem('afl_column_visibility', JSON.stringify(s)); }
        if (settings.columnSizing) { setColumnSizing(settings.columnSizing); localStorage.setItem('afl_column_sizing', JSON.stringify(settings.columnSizing)); }
        if (settings.columnPinning) { setColumnPinning(settings.columnPinning); localStorage.setItem('afl_column_pinning', JSON.stringify(settings.columnPinning)); }
        if (settings.density) { setDensity(settings.density); localStorage.setItem('afl_density', settings.density); }
        if (settings.compactHeaders !== undefined) { setCompactHeaders(settings.compactHeaders); localStorage.setItem('afl_compact_headers', settings.compactHeaders ? '1' : '0'); }
        if (settings.zebraRows !== undefined) { setZebraRows(settings.zebraRows); localStorage.setItem('afl_zebra_rows', settings.zebraRows ? '1' : '0'); }
        if (settings.sorting) setSorting(settings.sorting);
        if (settings.pageSize) setPagination((prev) => ({ ...prev, pageSize: settings.pageSize }));
        if (settings.lineTypeFilter) setLineTypeFilter(settings.lineTypeFilter);
        if (settings.linePayFilter) setLinePayFilter(settings.linePayFilter);
        if (settings.coveredLineFilter) setCoveredLineFilter(settings.coveredLineFilter);
        if (settings.columnFilters) setColumnFilters(sanitizeColumnFilters(settings.columnFilters));
        if (settings.savedViews) {
          const validated = settings.savedViews.map((v: unknown) => normalizeSavedViewConfig(v)).filter((v: any): v is SavedViewState => v !== null && 'name' in v);
          const pruned = pruneOldViews(deduplicateViews(validated), MAX_SAVED_VIEWS);
          setSavedViews(pruned);
          localStorage.setItem('afl_saved_views', JSON.stringify(pruned));
        }
        if (settings.selectedViewName) { setSelectedViewName(settings.selectedViewName); localStorage.setItem('afl_selected_view', settings.selectedViewName); }
        // Mark hydration complete — allows save effect and data fetching to start
        hydratedRef.current = true;
        setHydrated(true);
      })
      .catch(() => {
        // Even on error, mark hydrated so saves aren't blocked forever
        hydratedRef.current = true;
        setHydrated(true);
      });
    return () => { cancelled = true; };
  }, [userId, user]);

  // Track pageSize in a ref so page-index changes don't trigger the save effect
  const pageSizeRef = useRef(pagination.pageSize);
  useEffect(() => { pageSizeRef.current = pagination.pageSize; }, [pagination.pageSize]);

  // Debounced save all state to server
  useEffect(() => {
    // Don't save until initial pref load completes — prevents ping-pong
    if (!hydrated) return;
    const h = setTimeout(() => {
      const payload = {
        date, globalFilter, columnOrder, columnVisibility, density, compactHeaders, zebraRows,
        pageSize: pageSizeRef.current, sorting, columnFilters, columnSizing, columnPinning,
        typeCodeFilter, lineTypeFilter, linePayFilter, coveredLineFilter, savedViews, selectedViewName,
      };
      if (user) {
        apiClient.post('/api/prefs/report/authenticated?reportId=afl-daily', { settings: payload }).catch(() => void 0);
      } else {
        fetch(new URL('/api/prefs/report?reportId=afl-daily', (import.meta.env.VITE_API_URL as string) || window.location.origin).toString(), {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'x-user-id': userId, 'x-tenant-slug': (import.meta.env.VITE_TENANT_SLUG as string) || 'ourisman-va_alexandria-ford' },
          body: JSON.stringify({ settings: payload }),
        }).catch(() => void 0);
      }
    }, 600);
    return () => clearTimeout(h);
  }, [hydrated, userId, user, date, globalFilter, columnOrder, columnVisibility, density, compactHeaders, zebraRows, sorting, columnFilters, columnSizing, columnPinning, typeCodeFilter, lineTypeFilter, linePayFilter, coveredLineFilter, savedViews, selectedViewName]);

  // ── Hooks ─────────────────────────────────────────────────────────────
  const viewManager = useReportViewManager({
    state: { date, globalFilter, sorting, columnVisibility, columnOrder, columnSizing, columnPinning, density, compactHeaders, zebraRows, pagination, filterState, providerSource },
    setters: { setDate, setGlobalFilter, setSorting, setColumnVisibility, setColumnOrder, setColumnSizing, setColumnPinning, setDensity, setCompactHeaders, setZebraRows, setPagination, setFilterState, setProviderSource, setError },
    tenantSlug: currentTenant?.slug,
    tenantId: currentTenant?.id,
    savedViews, setSavedViews, selectedViewName, setSelectedViewName,
    sharedViews, viewTemplates, userPermissions,
    createSharedView, updateSharedView, deleteSharedView, trackViewUsage, refreshViewTemplates, updateViewTemplate,
    lastManualFiltersRef,
  });

  const reportData = useReportData({
    date, pagination, sorting, globalFilter, filterState, providerSource,
    tenantSlug: currentTenant?.slug,
    columnFilters, columnVisibility, columnOrder,
    user, userId,
    enabled: hydrated,
  });

  // Disable tenant selector while report data is loading
  useEffect(() => {
    setSwitching(reportData.loading);
  }, [reportData.loading, setSwitching]);

  const dynamicColumns = useDynamicColumns({
    baseColumns, facets: reportData.facets, theme, setGlobalFilter, setPagination,
  });

  // ── Stable toolbar handlers
  // Extracted from the toolbar JSX so ReportTopToolbar (React.memo) can bail
  // out of re-renders when unrelated Reports state changes.
  const {
    applySavedView,
    handleProviderChange,
    handleExistingTemplateUpdate,
    handleSaveSharedView,
    getCurrentViewPayload,
  } = viewManager;

  const handleZebraRowsChange = useCallback((v: boolean) => {
    setZebraRows(v);
    localStorage.setItem('afl_zebra_rows', v ? '1' : '0');
  }, []);

  const handleCompactHeadersChange = useCallback((v: boolean) => {
    setCompactHeaders(v);
    localStorage.setItem('afl_compact_headers', v ? '1' : '0');
    document.documentElement.toggleAttribute('data-compact-headers', v);
  }, []);

  const handleViewChange = useCallback((name: string) => {
    if (!name) {
      setSelectedViewName('');
      localStorage.removeItem('afl_selected_view');
      return;
    }
    setSelectedViewName(name);
    localStorage.setItem('afl_selected_view', name);
    const v = savedViews.find((s) => s.name === name);
    if (v) applySavedView(v);
  }, [savedViews, applySavedView]);

  const handleSaveView = useCallback(async ({ name, description, share }: { name: string; description?: string; share?: boolean }) => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('A name is required to save a view');
    const existingTemplate = viewTemplates.find((t) => t.name === trimmed);
    if (existingTemplate) { await handleExistingTemplateUpdate(existingTemplate, trimmed, description); return; }
    if (share && userPermissions.canShareViews) { await handleSaveSharedView(trimmed, description); return; }
    const view: SavedView = { name: trimmed, description: description?.trim() || undefined, ...getCurrentViewPayload() };
    const next = pruneOldViews([...savedViews.filter((v) => v.name !== trimmed), view], MAX_SAVED_VIEWS);
    setSavedViews(next); setSelectedViewName(trimmed);
    localStorage.setItem('afl_selected_view', trimmed);
    localStorage.setItem('afl_saved_views', JSON.stringify(next));
    applySavedView(view);
  }, [viewTemplates, userPermissions.canShareViews, savedViews, handleExistingTemplateUpdate, handleSaveSharedView, getCurrentViewPayload, applySavedView]);

  const handleDeleteView = useCallback(async () => {
    if (!selectedViewName) return;
    if (selectedViewName.startsWith('shared:')) {
      const sharedId = selectedViewName.replace('shared:', '');
      const shared = sharedViews.find((v) => v.id === sharedId);
      if (!shared) { showError('Unable to find shared view to delete.'); return; }
      if (!window.confirm(`Delete shared view "${shared.name}" for everyone?`)) return;
      try { const r = await deleteSharedView(shared.id); if (!r.success) throw new Error(r.error); } catch (e: any) { showError(e?.message || 'Failed to delete shared view'); return; }
      setSelectedViewName(''); localStorage.setItem('afl_selected_view', '');
      return;
    }
    if (selectedViewName.startsWith('[Shared] ')) {
      const sharedName = selectedViewName.replace('[Shared] ', '');
      const shared = sharedViews.find((v) => v.name === sharedName);
      if (!shared) { showError('Unable to find shared view to delete.'); return; }
      if (!window.confirm(`Delete shared view "${shared.name}" for everyone?`)) return;
      try { const r = await deleteSharedView(shared.id); if (!r.success) throw new Error(r.error); } catch (e: any) { showError(e?.message || 'Failed to delete shared view'); return; }
      setSelectedViewName(''); localStorage.setItem('afl_selected_view', '');
      return;
    }
    if (selectedViewName.startsWith('template:') || selectedViewName.startsWith('[Template] ')) {
      showError('Templates cannot be deleted. Please contact an administrator.');
      return;
    }
    const next = savedViews.filter((v) => v.name !== selectedViewName);
    setSavedViews(next); setSelectedViewName('');
    localStorage.setItem('afl_saved_views', JSON.stringify(next));
    localStorage.setItem('afl_selected_view', '');
  }, [selectedViewName, sharedViews, savedViews, deleteSharedView, showError]);

  const handleReset = useCallback(() => {
    setFilterState(createFilterState({ typeCodeFilter: [], lineTypeFilter: [], linePayFilter: [], coveredLineFilter: [], columnFilters: [] }));
    setGlobalFilter('');
    const defaultOrder = getDefaultColumnOrder();
    setColumnOrder(defaultOrder);
    localStorage.setItem('afl_column_order', JSON.stringify(defaultOrder));
    const defaultVis = getInitialColumnVisibility(true);
    setColumnVisibility(defaultVis);
    localStorage.setItem('afl_column_visibility', JSON.stringify(defaultVis));
    setPagination((prev) => ({ pageIndex: 0, pageSize: prev.pageSize }));
    handleProviderChange('opentrack');
  }, [handleProviderChange]);

  const table = useReportTable({
    columns: dynamicColumns,
    data: reportData.rows,
    total: reportData.total,
    loading: reportData.loading,
    state: { globalFilter, pagination, sorting, columnVisibility, density, columnOrder, columnFilters, columnSizing, columnPinning },
    setters: { setGlobalFilter, setPagination, setSorting, setColumnFilters, setColumnOrder, setColumnVisibility, setDensity, setColumnSizing, setColumnPinning },
    zebraRows,
    renderTopToolbarCustomActions: () => (
      <ReportTopToolbar
        providerSource={providerSource}
        onProviderChange={viewManager.handleProviderChange}
        dateRange={date}
        tenantId={currentTenant?.id}
        loading={reportData.loading}
        isInitialLoad={reportData.isInitialLoad}
        facets={reportData.facets}
        filterState={filterState}
        onTypeCodeFilterChange={setTypeCodeFilter}
        onLineTypeFilterChange={setLineTypeFilter}
        onLinePayFilterChange={setLinePayFilter}
        onCoveredLineFilterChange={setCoveredLineFilter}
        onDateChange={setDate}
        exportQueryParams={reportData.exportQueryParams}
        zebraRows={zebraRows}
        onZebraRowsChange={handleZebraRowsChange}
        compactHeaders={compactHeaders}
        onCompactHeadersChange={handleCompactHeadersChange}
        savedViews={savedViews}
        selectedViewName={selectedViewName}
        onViewChange={handleViewChange}
        onSaveView={handleSaveView}
        onDeleteView={handleDeleteView}
        onReset={handleReset}
        sharedViews={sharedViews}
        viewTemplates={viewTemplates}
        canShareViews={userPermissions.canShareViews}
        onSaveSharedView={viewManager.handleSaveSharedView}
        onApplySharedView={viewManager.handleApplySharedView}
        tenantName={currentTenant?.name}
      />
    ),
  });

  // ── Metrics ───────────────────────────────────────────────────────────
  const zebraAlt = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const metrics = useMemo(() => [
    { label: 'OLSG ROs', value: formatMetricValue(reportData.summary?.roCountFiltered ?? 0, 'number'), subtitle: `Total ROs: ${formatMetricValue(reportData.summary?.roCount ?? 0, 'number')}` },
    { label: 'Service Premium', value: formatMetricValue(reportData.summary?.['Labor Premium'] ?? 0, 'currency'), subtitle: `Sales: ${formatMetricValue(reportData.summary?.['Serv Sales'] ?? 0, 'currency')}` },
    { label: 'Parts Premium', value: formatMetricValue(reportData.summary?.['Parts Premium'] ?? 0, 'currency'), subtitle: `Sales: ${formatMetricValue(reportData.summary?.['Parts Sales'] ?? 0, 'currency')}` },
  ], [reportData.summary]);

  // Covered / not-covered line counts for the inline strip near the table.
  // Sourced from the query summary so they reflect the SAME filtered set as `total`
  // (facets ignore column filters + search, so they can't match the table).
  const coverageCounts = useMemo(() => {
    const covered = reportData.summary?.coveredCount ?? 0;
    const notCovered = reportData.summary?.notCoveredCount ?? 0;
    // Remainder of the filtered set whose coverage is neither true nor false
    // (coveredLine is blank/NULL — e.g. part rows). Keeps the three buckets == Total.
    const notClassified = Math.max(0, (reportData.total ?? 0) - covered - notCovered);
    return { covered, notCovered, notClassified };
  }, [reportData.summary, reportData.total]);

  // Active filters as removable pills — pairs with the counts so it's clear
  // which filters produced them. Removing a pill updates the data (and counts).
  const activeFilters = useMemo(() => {
    const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
    const coverageLabel = (v: string) =>
      v === 'true' ? 'Covered' : v === 'false' ? 'Not Covered' : '(blank)';
    const addMulti = (
      label: string,
      values: string[],
      setter: (next: string[]) => void,
      fmt: (v: string) => string = (v) => v || '(blank)',
    ) => {
      values.forEach((v) =>
        chips.push({
          key: `${label}:${v}`,
          label: `${label}: ${fmt(v)}`,
          onRemove: () => setter(values.filter((x) => x !== v)),
        }),
      );
    };
    addMulti('Type Code', typeCodeFilter, setTypeCodeFilter);
    addMulti('Line Type', lineTypeFilter, setLineTypeFilter);
    addMulti('Line Pay', linePayFilter, setLinePayFilter);
    addMulti('Covered Line', coveredLineFilter, setCoveredLineFilter, coverageLabel);
    columnFilters.forEach((f) => {
      const val = Array.isArray(f.value) ? f.value.join(', ') : String(f.value ?? '');
      if (!val) return;
      chips.push({
        key: `col:${f.id}`,
        label: `${f.id}: ${val}`,
        onRemove: () => setColumnFilters((prev) => prev.filter((c) => c.id !== f.id)),
      });
    });
    if (globalFilter.trim()) {
      chips.push({
        key: 'search',
        label: `Search: ${globalFilter.trim()}`,
        onRemove: () => setGlobalFilter(''),
      });
    }
    return chips;
  }, [
    typeCodeFilter,
    lineTypeFilter,
    linePayFilter,
    coveredLineFilter,
    columnFilters,
    globalFilter,
    setTypeCodeFilter,
    setLineTypeFilter,
    setLinePayFilter,
    setCoveredLineFilter,
    setColumnFilters,
    setGlobalFilter,
  ]);

  const clearAllFilters = useCallback(() => {
    setTypeCodeFilter([]);
    setLineTypeFilter([]);
    setLinePayFilter([]);
    setCoveredLineFilter([]);
    setColumnFilters([]);
    setGlobalFilter('');
  }, [
    setTypeCodeFilter,
    setLineTypeFilter,
    setLinePayFilter,
    setCoveredLineFilter,
    setColumnFilters,
    setGlobalFilter,
  ]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div data-testid="reports-container" data-zebra={zebraRows ? '1' : '0'} style={{ padding: '0' }}>
      <style>{`
        :root[data-compact-headers] .Mui-TableHeadCell-Content,
        :root[data-compact-headers] .Mui-TableHeadCell-Content-Labels { white-space: normal; line-height: 1.1; word-break: break-word; overflow: visible; text-overflow: unset; }
        :root[data-compact-headers] .MuiTableSortLabel-icon { display: none; }
        :root[data-compact-headers] .Mui-TableHeadCell-Content-Wrapper { padding-top: 4px; padding-bottom: 4px; }
        :root[data-compact-headers] .Mui-TableHeadCell-Content { gap: 2px; }
        :root { --afl-row-alt-bg: ${zebraAlt}; }
      `}</style>

      <datalist id="afl-search-columns">
        <option value="Date:" /><option value="RO Number:" /><option value="Last Name:" />
        <option value="VIN:" /><option value="Type Code:" /><option value="Op Code:" />
        <option value="Line Number:" /><option value="Category:" /><option value="Line Type:" />
        <option value="Line Pay Method:" /><option value="Service Type:" /><option value="Labor Amount:" />
        <option value="Part Number:" /><option value="TansGroup:" /><option value="TransCode:" />
        <option value="Manufacturer:" /><option value="Description:" /><option value="Comment:" />
        <option value="Net Price:" /><option value="Serv Sales:" /><option value="Serv Prem:" />
        <option value="Parts Sales:" /><option value="Parts Premium:" /><option value="coveredLine:" />
        <option value="coveredPart:" /><option value="Store:" />
        <option value="First Name:" /><option value="Home Phone:" /><option value="Business Phone:" />
        <option value="Address 1:" /><option value="Address 2:" /><option value="City:" />
        <option value="State:" /><option value="Zip Code:" /><option value="ADV NAME:" />
      </datalist>

      {(reportData.error || error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Failed to load report data</strong><br />{reportData.error || error}
        </Alert>
      )}

      {reportData.summary && <ReportMetricsBar metrics={metrics} loading={reportData.loading} />}

      {(reportData.summary || activeFilters.length > 0) && (
        <Box
          data-testid="report-filter-bar"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            px: 1.5,
            py: 0.75,
            mb: 0.75,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            backgroundColor: theme.palette.background.paper,
            fontSize: '0.8rem',
          }}
        >
          {reportData.summary && (
            <Box
              data-testid="coverage-counts"
              sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box component="span">
                Total:&nbsp;<strong>{(reportData.total ?? 0).toLocaleString()}</strong>
              </Box>
              <Box component="span" sx={{ color: theme.palette.success.main }}>
                Covered:&nbsp;<strong>{coverageCounts.covered.toLocaleString()}</strong>
              </Box>
              <Box component="span" sx={{ color: theme.palette.error.main }}>
                Not Covered:&nbsp;<strong>{coverageCounts.notCovered.toLocaleString()}</strong>
              </Box>
              <Box component="span" sx={{ color: theme.palette.text.secondary }}>
                Not Classified:&nbsp;<strong>{coverageCounts.notClassified.toLocaleString()}</strong>
              </Box>
            </Box>
          )}

          {activeFilters.length > 0 && (
            <>
              {reportData.summary && (
                <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'divider' }} />
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                {activeFilters.map((f) => (
                  <Chip
                    key={f.key}
                    label={f.label}
                    size="small"
                    variant="outlined"
                    onDelete={f.onRemove}
                    sx={{ height: 22, fontSize: '0.72rem' }}
                  />
                ))}
                <Chip
                  label="Clear all"
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={clearAllFilters}
                  sx={{ height: 22, fontSize: '0.72rem' }}
                />
              </Box>
            </>
          )}
        </Box>
      )}

      <MaterialReactTable table={table} />
    </div>
  );
};
